import type { HttpFailureKind, HttpRequestError } from '@ns/http-request';
import {
  isHttpError,
  isHttpRequestError,
  isHttpThrottleError,
} from '@ns/http-request';

/** The shared refusal body emitted by `msgraph-proxy`. */
export interface ProxyRefusalBody {
  /** The refusal fields emitted by the proxy's stable error envelope. */
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly source: 'msgraph-proxy';
  };
}

/** An `@ns/http-request` failure containing the shared proxy-refusal body. */
export type ProxyRefusal = HttpRequestError & {
  readonly body: ProxyRefusalBody;
};

/**
 * Identifies a refusal written by the shared `msgraph-proxy` refusal-body contract.
 *
 * @param error - A value caught from a client call.
 * @returns Whether the error is a proxy refusal with a typed refusal body.
 */
export function isProxyRefusal(error: unknown): error is ProxyRefusal {
  if (
    !isHttpRequestError(error) ||
    typeof error.body !== 'object' ||
    error.body === null
  )
    return false;
  const inner = 'error' in error.body ? error.body.error : undefined;
  return (
    typeof inner === 'object' &&
    inner !== null &&
    'source' in inner &&
    inner.source === 'msgraph-proxy' &&
    'code' in inner &&
    typeof inner.code === 'string' &&
    'message' in inner &&
    typeof inner.message === 'string'
  );
}

/** A failure described in the terms `@ns/plugin-picker-shared` classifies. */
export interface FailureDescription {
  /** The coarse family of the failure, as `@ns/http-request` names it. */
  readonly kind: HttpFailureKind;
  /** The `error.code` of a refusal written by the proxy, when it was one. */
  readonly refusalCode?: string;
  /** The status the upstream answered with, when there was an answer. */
  readonly status?: number;
}

/**
 * Describes a caught error in the terms a submit validator classifies.
 *
 * @remarks
 * A throttle is recognised by its class, like `isHttpThrottleError` does, and
 * described as the 429 it stands for.
 *
 * @param error - Whatever a `catch` received.
 * @returns The description, or `undefined` when the error is no failure of this client.
 */
export function describeFailure(
  error: unknown,
): FailureDescription | undefined {
  if (isProxyRefusal(error)) {
    return {
      kind: 'http',
      refusalCode: error.body.error.code,
      status: error.status,
    };
  }
  if (isHttpThrottleError(error)) return { kind: 'http', status: 429 };
  if (isHttpRequestError(error)) return { kind: 'http', status: error.status };
  if (isHttpError(error)) return { kind: error.failureKind };
  return undefined;
}

/**
 * Reads a provider error code from the shared Graph error body without changing it.
 *
 * It never reads a proxy-refusal body because that body follows the shared refusal contract rather than a provider error shape.
 *
 * @param error - A value caught from a client call.
 * @returns The provider code when the shared HTTP error body exposes one.
 */
export function providerCodeOf(error: unknown): string | undefined {
  if (
    !isHttpRequestError(error) ||
    isProxyRefusal(error) ||
    typeof error.body !== 'object' ||
    error.body === null
  )
    return undefined;
  const provider = 'error' in error.body ? error.body.error : undefined;
  if (typeof provider !== 'object' || provider === null) return undefined;
  const code = 'code' in provider ? provider.code : undefined;
  return typeof code === 'string' ? code : undefined;
}
